import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AES, Base64, CBC, CipherParams, Pkcs7, Utf8 } from 'crypto-es';
import { ToastService } from '@app/services/toast.service';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { KeyService } from './key.service';

const VALID_KEY_LENGTHS = [16, 24, 32];

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastService);
  private readonly keyService = inject(KeyService);

  /** Key derived from the current token (empty string when no token yet). */
  private getIJ(): Observable<string> {
    return this.keyService.getToken().pipe(
      take(1),
      switchMap(token => of(token?.trim() ? token.replace('VGtmE', '$@9#k') : ''))
    );
  }

  encryptString(plainText: string): string {
    let result = '';
    this.getIJ().subscribe(IJ => {
      if (!VALID_KEY_LENGTHS.includes(IJ.length)) {
        throw new Error('Key must be 16, 24, or 32 bytes long');
      }
      const key = Utf8.parse(IJ);
      const encrypted = AES.encrypt(plainText, key, {
        iv: key,
        mode: CBC,
        padding: Pkcs7
      });
      result = encrypted.ciphertext.toString(Base64);
    });
    return result;
  }

  decryptString(encryptedBase64: string): string {
    let result = '';
    this.getIJ().subscribe(IJ => {
      if (!VALID_KEY_LENGTHS.includes(IJ.length)) {
        return;
      }
      const decryptedText = this.decryptWithKey(IJ, encryptedBase64);
      if (!decryptedText) {
        throw new Error('Decryption failed');
      }
      result = decryptedText;
    });
    return result;
  }

  decryptStringMask(encryptedBase64: string, isMasking = true): string | null {
    if (!encryptedBase64) {
      return null;
    }
    let result: string | null = null;
    this.getIJ().subscribe(IJ => {
      if (!VALID_KEY_LENGTHS.includes(IJ.length)) {
        return;
      }
      const decryptedText = this.decryptWithKey(IJ, encryptedBase64);
      if (!decryptedText) {
        result = this.createMasking(encryptedBase64);
      } else {
        result = isMasking ? this.createMasking(decryptedText) : decryptedText;
      }
    });
    return result;
  }

  createMasking(val: string | null | undefined): string | null {
    if (!val) {
      return null;
    }
    if (val.length <= 2) {
      return val;
    }
    return '*'.repeat(val.length - 2) + val.slice(-2);
  }

  setToLocalStorage(key: string, value: string): void {
    localStorage.setItem(key, this.encryptString(value));
  }

  getFromLocalStorage(key: string): string | null {
    const encryptedValue = localStorage.getItem(key);
    if (!encryptedValue) {
      return null;
    }
    try {
      return this.decryptString(encryptedValue);
    } catch (error) {
      console.log('Invalid localStorage data, decryption failed:', error);
      this.clearLocalStorageAndRedirect();
      return null;
    }
  }

  /** Shared AES-CBC decrypt used by decryptString and decryptStringMask. */
  private decryptWithKey(IJ: string, encryptedBase64: string): string {
    try {
      const key = Utf8.parse(IJ);
      const cipherParams = CipherParams.create({
        ciphertext: Base64.parse(encryptedBase64),
        iv: key
      });
      const decrypted = AES.decrypt(cipherParams, key, {
        iv: key,
        mode: CBC,
        padding: Pkcs7
      });
      return decrypted.toString(Utf8);
    } catch (error) {
      console.log('Error during decryption:', error);
      throw new Error('Decryption failed');
    }
  }

  private clearLocalStorageAndRedirect(): void {
    localStorage.clear();
    this.toastr.error('Something Went wrong, Please login again.', 'Session Expired', {
      timeOut: 3000
    });
    this.router.navigate(['/login']);
  }
}
